import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { VehicleFormGroup } from './vehicle-form-group.model';

export interface CustomerFormGroup {
    id: FormControl<string | null>;
    tenantId: FormControl<string>;
    addressId: FormControl<string | null>;
    address: FormGroup<AddressFormGroup | null>;
    contacts: FormArray<FormGroup<ContactFormGroup>>;
    name: FormControl<string | null>;
    surname: FormControl<string | null>;
    companyName: FormControl<string | null>;
    primaryContact?: FormGroup<ContactFormGroup | null>;
    vehicles?: FormArray<FormGroup<VehicleFormGroup>>;
    quotes?: FormArray;
    inspections?: FormArray;
}

export interface AddressFormGroup {
    id: FormControl<string | null>;
    tenantId: FormControl<string>;
    line1: FormControl<string | null>;
    line2: FormControl<string | null>;
    city: FormControl<string | null>;
    state: FormControl<string>;
    zip: FormControl<string | null>;
}

export interface ContactFormGroup {
    id: FormControl<string | null>;
    tenantId: FormControl<string>;
    customerId: FormControl<string | null>;
    email: FormControl<string | null>;
    isPrimary: FormControl<boolean>;
    name: FormControl<string | null>;
    phoneNumber: FormControl<string | null>;
    typeId: FormControl<string | null>;
    isTextApproved: FormControl<boolean>;
}

export function requiredContactPhoneNumberOrEmail(control: AbstractControl): ValidationErrors | null {
    if (control) {
        const phoneNumber = control.get('phoneNumber')?.value;
        const email = control.get('email')?.value;
        if ((phoneNumber && phoneNumber.trim().length > 0) || (email && email.trim().length > 0)) {
            return null;
        }
    }
    return { required: { valid: false, message: 'Either phone number or email is required.' } };
}

/**
 * Group validator factory for the customer name fields. The rule depends on whether the tenant's
 * Structured Customer Name setting is on, which is only known at runtime, so the validator is applied
 * dynamically once the setting settles rather than baked into the group at construction.
 *
 * @param structured When true (setting on) the group is valid if any of name/surname/companyName has a
 *   trimmed value; the `name` control carries the given name. When false (setting off) only a value in
 *   `name` satisfies the rule -- a company name alone must not.
 * @returns A validator that flags the group with a `required` error when no owning name field is filled.
 */
export function customerNameRequired(structured: boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const hasValue = (key: string): boolean => ((control.get(key)?.value as string | null | undefined)?.trim().length ?? 0) > 0;
        const isValid = structured ? ['name', 'surname', 'companyName'].some(hasValue) : hasValue('name');
        return isValid ? null : { required: { valid: false, message: 'Name is required.' } };
    };
}
